import { orderBy } from 'lodash-es';

import {
  AGE,
  CITY,
  CREATED_AT,
  EMAIL,
  NAME,
  PHONE_NUMBER,
} from 'src/common/constants/participantSortKeys';
import dayjs from 'src/helpers/dayjs';
import { EOrder } from 'src/interfaces/Order';
import { IEnrichedParticipant, IParticipant } from 'src/interfaces/Participant';
import { getParticipantDisplayAge } from 'src/helpers/registrationPrice';
import { checkForAllWords } from 'src/helpers/search';

export type SortParticipantsOptions = {
  editionYear?: number | null;
  editionDate?: Date | string | null;
};

type RegistrationTimeSource = {
  id?: string;
  createdAt?: string | Date | null;
} | null | undefined;

function createdAtMs(value: string | Date | null | undefined): number | null {
  if (value == null || value === '') {
    return null;
  }
  const ms = dayjs(value).valueOf();
  return Number.isNaN(ms) ? null : ms;
}

/** Prefer `registration.createdAt`, then `participant.createdAt` (legacy check-in-only editions). */
export function registrationCreatedAtMs(
  registration: RegistrationTimeSource,
  participantCreatedAt?: string | Date | null,
): number | null {
  return createdAtMs(registration?.createdAt) ?? createdAtMs(participantCreatedAt);
}

/** Date for tooltips / period filter — registration.createdAt, else participant.createdAt. */
export function registrationSignupAt(
  registration: RegistrationTimeSource,
  participantCreatedAt?: string | Date | null,
): Date | null {
  const ms = registrationCreatedAtMs(registration, participantCreatedAt);
  return ms == null ? null : new Date(ms);
}

function toSignupTimestamp(participant: IParticipant): number | null {
  const enriched = participant as IEnrichedParticipant;
  return registrationCreatedAtMs(enriched.registration, enriched.createdAt);
}

/** UTC calendar day `YYYY-MM-DD` — matches signup timeline grouping. */
export function registrationUtcDayKey(value: string | Date): string | null {
  const day = dayjs(value);
  if (!day.isValid()) {
    return null;
  }
  return day.utc().format('YYYY-MM-DD');
}

export function matchesParticipantQuery(
  query: string,
  participant: Pick<IParticipant, 'firstName' | 'lastName' | 'email' | 'phoneNumber'>,
): boolean {
  if (!query) {
    return true;
  }
  return (
    !!checkForAllWords(query, `${participant.firstName}${participant.lastName}`) ||
    !!checkForAllWords(query, participant.email) ||
    !!checkForAllWords(query, participant.phoneNumber)
  );
}

function normalizeFilterList(value: readonly string[] | string | null | undefined): string[] {
  if (value == null || value === '') {
    return [];
  }
  if (typeof value === 'string') {
    return [value];
  }
  return [...value];
}

export function matchesCompetitionDistanceFilters(
  competitionFilter: readonly string[] | string | null,
  distanceFilter: readonly string[] | string | null,
  competitionId: string,
  distanceId: string,
): boolean {
  const competitions = normalizeFilterList(competitionFilter);
  const distances = normalizeFilterList(distanceFilter);
  return (
    (competitions.length === 0 || competitions.includes(competitionId)) &&
    (distances.length === 0 || distances.includes(distanceId))
  );
}

type ParticipantRegistrationState = 'unregistered' | 'registered' | 'checked_in';

export function isParticipantCheckedIn(
  participant: IEnrichedParticipant,
  checkinsById?: Record<string, unknown>,
): boolean {
  if (participant.registration?.status === 'checked_in') {
    return true;
  }
  if (participant.checkin?.id) {
    return true;
  }
  // Legacy participant.checkedIn is only trusted when resolved in the checkins store
  // (edition-scoped list API embeds checkin on the row instead).
  if (participant.checkedIn && checkinsById) {
    return !!checkinsById[participant.checkedIn];
  }
  return false;
}

export function getParticipantRegistrationState(
  participant: IEnrichedParticipant,
  checkinsById?: Record<string, unknown>,
): ParticipantRegistrationState {
  if (isParticipantCheckedIn(participant, checkinsById)) {
    return 'checked_in';
  }
  if (participant.registration?.id) {
    return 'registered';
  }
  return 'unregistered';
}

export function getParticipantEntryCompetitionDistanceIds(participant: IEnrichedParticipant): {
  competitionId: string;
  distanceId: string;
} {
  return {
    competitionId:
      participant.registration?.competitionId || participant.checkin?.competition || '',
    distanceId: participant.registration?.distanceId || participant.checkin?.distance || '',
  };
}

export function matchesCheckedInFilter(
  checkedInFilter: readonly string[] | string | null,
  isCheckedIn: boolean,
): boolean {
  const selected = normalizeFilterList(checkedInFilter);
  if (selected.length === 0) {
    return true;
  }
  const wantsCheckedIn = selected.includes('true');
  const wantsNotCheckedIn = selected.includes('false');
  if (wantsCheckedIn && wantsNotCheckedIn) {
    return true;
  }
  if (wantsCheckedIn) {
    return isCheckedIn;
  }
  if (wantsNotCheckedIn) {
    return !isCheckedIn;
  }
  return true;
}

/**
 * Inclusive range on registration `createdAt`.
 * Bounds may be ISO datetimes or legacy `YYYY-MM-DD` (treated as UTC day start/end).
 * Unknown time (`atMs` null) does not exclude — callers must require a real signup time.
 */
export function matchesRegistrationDateRange(
  atMs: number | null,
  registeredFrom: string | null | undefined,
  registeredTo: string | null | undefined,
): boolean {
  if (!registeredFrom && !registeredTo) {
    return true;
  }
  if (atMs == null) {
    return true;
  }

  if (registeredFrom) {
    const from = boundStartMs(registeredFrom);
    if (from != null && atMs < from) {
      return false;
    }
  }
  if (registeredTo) {
    const to = boundEndMs(registeredTo);
    if (to != null && atMs > to) {
      return false;
    }
  }
  return true;
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function boundStartMs(value: string): number | null {
  if (DATE_ONLY_RE.test(value)) {
    const day = dayjs.utc(value).startOf('day');
    return day.isValid() ? day.valueOf() : null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : null;
}

function boundEndMs(value: string): number | null {
  if (DATE_ONLY_RE.test(value)) {
    const day = dayjs.utc(value).endOf('day');
    return day.isValid() ? day.valueOf() : null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : null;
}

/**
 * Suggested bounds for the advanced registration-period filter shortcuts.
 * Editions have no signup-open timestamp — use Jan 1 of the *previous* calendar year
 * (signups often open before Jan 1 of the race year) and the event day as “to”.
 * When `date` is missing, fall back to end of the edition year (UTC).
 */
export function getEditionRegistrationPeriodDefaults(edition: {
  year: number;
  date?: Date | string | null;
}): { registeredFrom: string; registeredTo: string } {
  const eventDay = edition.date != null && edition.date !== '' ? dayjs(edition.date) : null;
  const registeredTo =
    eventDay != null && eventDay.isValid()
      ? eventDay.endOf('day').toISOString()
      : dayjs.utc(`${edition.year}-12-31`).endOf('day').toISOString();

  return {
    registeredFrom: dayjs.utc(`${edition.year - 1}-01-01`).startOf('day').toISOString(),
    registeredTo,
  };
}

export function getRegistrationIdsFromParticipants(
  participants: IEnrichedParticipant[],
): Record<string, string> {
  const map: Record<string, string> = {};
  participants.forEach(p => {
    if (p.registration?.id) {
      map[p.id] = p.registration.id;
    }
  });
  return map;
}

export function filterAdminParticipants(
  participants: IParticipant[],
  {
    query,
    competitionFilter,
    distanceFilter,
    checkedInFilter,
    registeredFrom = null,
    registeredTo = null,
  }: {
    query: string;
    competitionFilter: readonly string[] | string | null;
    distanceFilter: readonly string[] | string | null;
    checkedInFilter: readonly string[] | string | null;
    registeredFrom?: string | null;
    registeredTo?: string | null;
  },
): IParticipant[] {
  return participants.filter(c => {
    if (!matchesParticipantQuery(query, c)) {
      return false;
    }

    const enriched = c as IEnrichedParticipant;
    const { competitionId, distanceId } = getParticipantEntryCompetitionDistanceIds(enriched);

    if (
      !matchesCompetitionDistanceFilters(
        competitionFilter,
        distanceFilter,
        competitionId,
        distanceId,
      )
    ) {
      return false;
    }

    if (!matchesCheckedInFilter(checkedInFilter, isParticipantCheckedIn(enriched))) {
      return false;
    }

    if (registeredFrom || registeredTo) {
      const atMs = registrationCreatedAtMs(enriched.registration, enriched.createdAt);
      if (atMs == null) {
        return false;
      }
      return matchesRegistrationDateRange(atMs, registeredFrom, registeredTo);
    }

    return true;
  });
}

export function sortParticipants<T extends IParticipant>(
  participants: T[],
  orderByKey: string,
  order: EOrder,
  options?: SortParticipantsOptions,
): T[] {
  const editionYear = options?.editionYear;
  const editionDate = options?.editionDate;

  if (orderByKey === NAME) {
    return orderBy(participants, [p => p.lastName || '', p => p.firstName || ''], [order, order]);
  }

  if (orderByKey === AGE) {
    return orderBy(
      participants,
      p => getParticipantDisplayAge(p, editionYear, editionDate),
      order,
    );
  }

  if (orderByKey === CREATED_AT) {
    return orderBy(participants, p => toSignupTimestamp(p), order);
  }

  if (orderByKey === CITY || orderByKey === EMAIL || orderByKey === PHONE_NUMBER) {
    return orderBy(
      participants,
      p => {
        const value = p[orderByKey as keyof IParticipant];
        return typeof value === 'string' ? value : '';
      },
      order,
    );
  }

  return orderBy(participants, orderByKey, order);
}
